import React from 'react';
import styled, { createGlobalStyle } from 'styled-components';
import Home from '../Home';

// --- Global Styles to Reset and Apply Base Font/Color ---

const GlobalStyle = createGlobalStyle`
  body {
    margin: 0;
    padding: 0;
    font-family: Arial, sans-serif;
    color: #333;
    line-height: 1.6;
    background-color: #ffffff;
  }
  *, *::before, *::after {
    box-sizing: border-box;
  }
`;

// --- Styled Components for the entire page structure ---

const PageContainer = styled.div`
  /* Mimics the overall page width and central flow */
  max-width: 100%;
  overflow-x: hidden;
`;

const Section = styled.section`
  padding: 60px 5%;
  text-align: center;
  /* Max width for content to prevent excessive stretching */
  margin: 0 auto;
  @media (min-width: 1024px) {
    padding: 80px 10%;
  }
`;

const DarkSection = styled(Section)`
  background-image: url('https://via.placeholder.com/1600x900?text=Dark+Section+Background+Image');
  background-size: cover;
  background-position: center;
  background-attachment: scroll;
  background-color: #1a1a1a; /* Fallback color */
  color: #fff;
  padding: 80px 5%;
  text-align: left;
  position: relative;
  z-index: 1;

  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background-color: rgba(0, 0, 0, 0.75); /* Dark overlay */
    z-index: -1;
  }
`;

const Footer = styled.footer`
  background-color: #f4f4f4;
  padding: 40px 5%;
  text-align: center;
  color: #666;
  font-size: 0.9em;
  @media (min-width: 1024px) {
    padding: 40px 10%;
  }
`;

// --- Styled Components for common elements ---

const Title = styled.h2`
  font-size: 1.8em;
  color: #333;
  margin-bottom: 20px;
  line-height: 1.2;
  @media (min-width: 768px) {
    font-size: 2.5em;
  }
`;

const Subtitle = styled.h3`
  font-size: 1.5em;
  margin-bottom: 20px;
  color: ${props => props.dark ? '#fff' : '#444'};
  text-transform: capitalize;
  @media (min-width: 768px) {
    font-size: 1.8em;
  }
`;

const Paragraph = styled.p`
  max-width: 900px;
  margin: 0 auto 20px auto;
  color: ${props => props.dark ? '#ccc' : '#555'};
  font-size: 1em;
`;

const Button = styled.a`
  display: inline-block;
  background-color: #ff5722; /* Red-Orange color from the image */
  color: white;
  padding: 10px 30px;
  margin: 20px 0;
  text-decoration: none;
  font-weight: bold;
  border-radius: 5px;
  transition: background-color 0.3s;
  cursor: pointer;
  text-transform: uppercase;
  font-size: 0.9em;

  &:hover {
    background-color: #e64a19;
  }
`;

// --- Section Specific Styled Components ---

// 1. Header/Hero Section
const HeroSection = styled(Section)`
  padding: 40px 5%;
  display: flex;
  flex-direction: column;
  text-align: center;
  gap: 40px;
  align-items: center;
  justify-content: center;
  @media (min-width: 768px) {
    flex-direction: row;
    text-align: left;
    padding: 60px 10%;
  }
`;

const HeroText = styled.div`
  flex: 1;
  max-width: 600px;
`;

const HeroImage = styled.div`
  /* Placeholder for the monitor/lollipop image */
  width: 100%;
  max-width: 300px;
  height: 250px;
  background: url('https://via.placeholder.com/300x250?text=Web+Traffic+Secrets') no-repeat center center / cover;
  border-radius: 10px;
  @media (min-width: 768px) {
    width: 300px;
  }
`;

// 2. Can Web Analytics boost your sales?
const BoostSection = styled(Section)`
  background-color: #ffffff;
  border-top: 1px solid #eee;
  padding-bottom: 60px;
`;

// 3. How does web analytics improve critical performance?
const PerformanceSection = styled(Section)`
  background-color: #f7f7f7;
  padding: 60px 5%;
`;

const PerformanceGrid = styled.div`
  display: flex;
  flex-direction: column;
  gap: 20px;
  max-width: 1000px;
  margin: 40px auto 0;
`;

const PerformanceBlock = styled.div`
  display: flex;
  flex-direction: column;
  background-color: #fff;
  border: 1px solid #ddd;
  box-shadow: 0 2px 5px rgba(0,0,0,0.1);
  padding: 0;
  text-align: left;
  
  @media (min-width: 768px) {
    flex-direction: row;
    align-items: center;
  }
`;

const BlockImage = styled.div`
  /* Placeholder for the block image */
  width: 100%;
  height: 150px;
  background: url(${props => props.bg}) no-repeat center center / cover;
  
  @media (min-width: 768px) {
    width: 250px;
    height: 120px;
    flex-shrink: 0;
  }
`;

const BlockContent = styled.div`
  flex: 1;
  padding: 15px 20px;
`;

const BlockTitle = styled.h4`
  font-size: 1.2em;
  color: #ff5722;
  margin: 0 0 5px 0;
`;

// 4. Why Choose Us (Dark Section)
const ChoiceGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr;
  gap: 20px;
  margin-top: 40px;
  
  @media (min-width: 768px) {
    grid-template-columns: repeat(2, 1fr);
    gap: 40px;
  }
`;

const ChoiceBlock = styled.div`
  border: 1px solid #555;
  padding: 25px;
  background-color: rgba(0,0,0,0.4);
  border-radius: 5px;
  text-align: left;
`;

const ChoiceTitle = styled.h4`
  color: #ff5722;
  font-size: 1.3em;
  margin-bottom: 10px;
  font-weight: bold;
`;

// 5. Why is analytics necessary?
const NecessitySection = styled(Section)`
  padding: 60px 5%;
  background-color: #fff;
`;

const NecessityList = styled.ul`
  list-style: none;
  padding: 0;
  max-width: 700px;
  margin: 40px auto;
  text-align: left;
`;

const NecessityItem = styled.li`
  display: flex;
  align-items: flex-start;
  margin-bottom: 25px;
  gap: 20px;
`;

const ItemNumber = styled.div`
  width: 30px;
  height: 30px;
  border: 2px solid #ff5722;
  border-radius: 50%;
  color: #ff5722;
  display: flex;
  align-items: center;
  justify-content: center;
  font-weight: bold;
  font-size: 1.1em;
  flex-shrink: 0;
  margin-top: 4px; /* Align number better with text */
`;

const ItemContent = styled.div`
  flex: 1;
`;

const ItemTitle = styled.h4`
  margin: 0 0 5px 0;
  font-size: 1.1em;
  color: #333;
`;

// 6. Footer Links
const FooterHeader = styled.h3`
  font-size: 1.5em;
  color: #444;
  text-transform: uppercase;
  margin-bottom: 30px;
`;

const FooterLinksGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr;
  gap: 20px;
  text-align: left;
  padding: 0 0 30px 0;

  @media (min-width: 768px) {
    grid-template-columns: repeat(4, 1fr);
  }
`;

const FooterLink = styled.a`
  color: #666;
  text-decoration: none;
  margin-bottom: 8px;
  display: block;
  font-size: 0.9em;

  &:hover {
    color: #ff5722;
  }
`;

const ContentMarketing = () => {
  return (
    <Home>
    <PageContainer>
      <GlobalStyle />
      {/* 1. Header/Hero Section */}
      <HeroSection>
        <HeroText>
          <Title>Unlocking The Secrets Of Web Traffic, Do You Really Know?</Title>
          <Paragraph>
            Web traffic is more than just clicks and page views. It's the pulse of your online business. Without accurate data, you're navigating blind. Web analytics provides the insights needed to understand your visitors, optimize your site, and drive growth. Learn how **SRK Software** helps you uncover the true potential of your data.
          </Paragraph>
          <Button href="#get-started">Get Started</Button>
        </HeroText>
        <HeroImage />
      </HeroSection>

      {/* 2. Can Web Analytics boost your sales? */}
      <BoostSection>
        <Title style={{ fontSize: '1.5em' }}>Are you aware that web analytics can fundamentally boost your sales?</Title>
        <Paragraph>
          Yes! By analyzing visitor behavior, you can identify high-converting paths, see where users drop off, and tailor your content to match customer intent. This targeted approach directly translates into higher conversion rates and increased sales. We give you the tools to succeed.
        </Paragraph>
        <Button href="#learn-more">Learn More</Button>
      </BoostSection>

      {/* 3. How does web analytics improve critical performance? */}
      <PerformanceSection>
        <Title>How does web analytics improve critical performance?</Title>
        <PerformanceGrid>
          <PerformanceBlock>
            <BlockImage bg="https://via.placeholder.com/250x120?text=Optimizing+Product+Performance" />
            <BlockContent>
              <BlockTitle>Optimizing Product Performance</BlockTitle>
              <Paragraph style={{ margin: 0, fontSize: '0.9em', color: '#333' }}>
                Identify which products or services perform best, understand user engagement, and make data-driven decisions on inventory, pricing, and promotion strategies.
              </Paragraph>
            </BlockContent>
          </PerformanceBlock>
          <PerformanceBlock>
            <BlockImage bg="https://via.placeholder.com/250x120?text=Crafting+Conversion+Strategies" />
            <BlockContent>
              <BlockTitle>Crafting Conversion Strategies</BlockTitle>
              <Paragraph style={{ margin: 0, fontSize: '0.9em', color: '#333' }}>
                Analyze your sales funnel to pinpoint bottlenecks and conversion killers. Use A/B testing insights to refine your path to purchase for maximum efficiency.
              </Paragraph>
            </BlockContent>
          </PerformanceBlock>
          <PerformanceBlock>
            <BlockImage bg="https://via.placeholder.com/250x120?text=Informed+Decision-Making" />
            <BlockContent>
              <BlockTitle>Informed Decision-Making</BlockTitle>
              <Paragraph style={{ margin: 0, fontSize: '0.9em', color: '#333' }}>
                Move beyond guesswork. Web analytics provides objective data on what works and what doesn't, allowing for smarter allocation of resources and marketing spend.
              </Paragraph>
            </BlockContent>
          </PerformanceBlock>
        </PerformanceGrid>
      </PerformanceSection>

      {/* 4. Why Choose Us (Dark Section) */}
      <DarkSection>
        <Subtitle dark style={{ textAlign: 'center' }}>Why Choose us</Subtitle>
        <ChoiceGrid>
          <ChoiceBlock>
            <ChoiceTitle>Intuitive Navigation</ChoiceTitle>
            <Paragraph dark style={{ margin: 0 }}>
              Our platform offers a clean, easy-to-use interface, making complex data accessible and actionable for all team members, regardless of technical skill.
            </Paragraph>
          </ChoiceBlock>
          <ChoiceBlock>
            <ChoiceTitle>Refinement | Sophistication</ChoiceTitle>
            <Paragraph dark style={{ margin: 0 }}>
              Leverage advanced segmentation and custom reporting to dive deeper into niche user groups and uncover subtle trends that competitors miss.
            </Paragraph>
          </ChoiceBlock>
          <ChoiceBlock>
            <ChoiceTitle>Increased Focus</ChoiceTitle>
            <Paragraph dark style={{ margin: 0 }}>
              By automating routine data collection, we allow your team to spend less time gathering data and more time acting on the insights, improving productivity.
            </Paragraph>
          </ChoiceBlock>
          <ChoiceBlock>
            <ChoiceTitle>Reliability | Transparency</ChoiceTitle>
            <Paragraph dark style={{ margin: 0 }}>
              Trust your data. Our tracking methods ensure high accuracy, and we provide clear documentation on how metrics are calculated, offering complete transparency.
            </Paragraph>
          </ChoiceBlock>
          <ChoiceBlock style={{ gridColumn: '1 / -1', textAlign: 'center' }}>
            <ChoiceTitle>Cutting-edge Appeal</ChoiceTitle>
            <Paragraph dark style={{ margin: 0, maxWidth: 'unset' }}>
              We continuously update our tools with the latest in machine learning and AI to give you a predictive edge, helping you anticipate market shifts and customer behavior.
            </Paragraph>
          </ChoiceBlock>
        </ChoiceGrid>
        <div style={{ textAlign: 'center' }}>
          <Button href="#contact">Contact Us Now</Button>
        </div>
      </DarkSection>

      {/* 5. Why is analytics necessary? */}
      <NecessitySection>
        <Subtitle>Why is analytics necessary?</Subtitle>
        <Paragraph>
          In today's digital landscape, understanding your user is paramount. Analytics is the bridge between raw data and business growth. It's necessary for:
        </Paragraph>
        <NecessityList>
          <NecessityItem>
            <ItemNumber>1</ItemNumber>
            <ItemContent>
              <ItemTitle>Informed Decision-Making</ItemTitle>
              <Paragraph style={{ margin: 0, fontSize: '0.9em', color: '#555' }}>
                Use objective data instead of intuition to guide all your marketing and development efforts.
              </Paragraph>
            </ItemContent>
          </NecessityItem>
          <NecessityItem>
            <ItemNumber>2</ItemNumber>
            <ItemContent>
              <ItemTitle>Understanding Audience Behavior</ItemTitle>
              <Paragraph style={{ margin: 0, fontSize: '0.9em', color: '#555' }}>
                See exactly what your visitors are doing, what they like, and what drives them away.
              </Paragraph>
            </ItemContent>
          </NecessityItem>
          <NecessityItem>
            <ItemNumber>3</ItemNumber>
            <ItemContent>
              <ItemTitle>Measuring Performance</ItemTitle>
              <Paragraph style={{ margin: 0, fontSize: '0.9em', color: '#555' }}>
                Track the success of campaigns, content, and site changes with quantifiable metrics like ROI and conversion rate.
              </Paragraph>
            </ItemContent>
          </NecessityItem>
          <NecessityItem>
            <ItemNumber>4</ItemNumber>
            <ItemContent>
              <ItemTitle>Optimizing Strategies</ItemTitle>
              <Paragraph style={{ margin: 0, fontSize: '0.9em', color: '#555' }}>
                Continuously test and refine your marketing and content strategies based on real-time user feedback.
              </Paragraph>
            </ItemContent>
          </NecessityItem>
          <NecessityItem>
            <ItemNumber>5</ItemNumber>
            <ItemContent>
              <ItemTitle>Unveiling Opportunities</ItemTitle>
              <Paragraph style={{ margin: 0, fontSize: '0.9em', color: '#555' }}>
                Identify untapped markets, potential product gaps, and new traffic sources that can fuel future growth.
              </Paragraph>
            </ItemContent>
          </NecessityItem>
          <NecessityItem>
            <ItemNumber>6</ItemNumber>
            <ItemContent>
              <ItemTitle>Return on Investment (ROI)</ItemTitle>
              <Paragraph style={{ margin: 0, fontSize: '0.9em', color: '#555' }}>
                Demonstrate the value of your efforts by clearly linking digital activities to tangible business outcomes and profit.
              </Paragraph>
            </ItemContent>
          </NecessityItem>
        </NecessityList>
        <Button href="#start-analysis">Start Analysis</Button>
      </NecessitySection>

      {/* 6. Footer */}
      <Footer>
        <FooterHeader>FAQ</FooterHeader>
        <FooterLinksGrid>
          <div>
            <FooterLink href="#">Web analytics definitions</FooterLink>
            <FooterLink href="#">Benefits of using **SRK Software**</FooterLink>
            <FooterLink href="#">Pricing and Packages</FooterLink>
            <FooterLink href="#">Security and Data Protection</FooterLink>
          </div>
          <div>
            <FooterLink href="#">Setting up tracking code</FooterLink>
            <FooterLink href="#">Data privacy and compliance</FooterLink>
            <FooterLink href="#">Customer Support</FooterLink>
            <FooterLink href="#">Training and Resources</FooterLink>
          </div>
          <div>
            <FooterLink href="#">Integration with other tools</FooterLink>
            <FooterLink href="#">Custom reporting options</FooterLink>
            <FooterLink href="#">API access</FooterLink>
            <FooterLink href="#">Mobile App Analytics</FooterLink>
          </div>
          <div>
            <FooterLink href="#">Careers at **SRK Software**</FooterLink>
            <FooterLink href="#">Our mission and vision</FooterLink>
            <FooterLink href="#">Terms of Service</FooterLink>
            <FooterLink href="#">Contact Sales</FooterLink>
          </div>
        </FooterLinksGrid>
        <Paragraph style={{ fontSize: '0.8em', margin: '20px 0 0 0', color: '#666' }}>
          &copy; {new Date().getFullYear()} **SRK Software**. All rights reserved.
        </Paragraph>
      </Footer>
    </PageContainer>
    </Home>
  );
};

export default ContentMarketing;

